// src/services/userPresenceService.ts
import { getApiUrl } from '../config/api';

interface TrackingData {
  url: string;
  pageTitle: string;
  referrer: string;
  sessionId: string;
  userAgent: string;
  country?: string;
  region?: string;
  city?: string;
  timezone?: string;
  deviceType?: string;
  browser?: string;
  duration?: number;
}

class UserPresenceService {
  private sessionId: string;
  private pageStartTime: number = 0;
  private isTracking: boolean = true;

  constructor() {
    this.sessionId = this.generateSessionId();
    //console.log('🔍 UserPresenceService initialized with session:', this.sessionId);
    this.initializeTracking();
  }

  private generateSessionId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `session_${timestamp}_${random}`;
  }

  private initializeTracking() {
    //console.log('🚀 Initializing user presence tracking...');
    
    // Track initial page load
    setTimeout(() => {
      this.trackPageVisit();
    }, 1000); // Small delay to ensure page is loaded
    
    // Track page duration when user leaves
    window.addEventListener('beforeunload', () => {
      this.trackPageDuration();
    });

    // Track visibility changes (tab switching)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackPageDuration();
      } else {
        this.pageStartTime = Date.now();
      }
    });

    // Track route changes in SPAs
    this.setupRouteChangeTracking();
  }

  private setupRouteChangeTracking() {
    let currentUrl = window.location.href;
    
    // Track initial URL
    //console.log('📍 Initial URL:', currentUrl);
    
    // Listen for popstate (back/forward buttons)
    window.addEventListener('popstate', () => {
      //console.log('📍 Popstate detected, URL changed from', currentUrl, 'to', window.location.href);
      this.trackPageDuration();
      currentUrl = window.location.href;
      setTimeout(() => this.trackPageVisit(), 100);
    });

    // Override pushState and replaceState to catch programmatic navigation
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(...args) {
      //console.log('📍 PushState detected');
      originalPushState.apply(history, args);
      setTimeout(() => {
        if (window.location.href !== currentUrl) {
          currentUrl = window.location.href;
          (window as any).userPresenceService?.trackPageVisit();
        }
      }, 100);
    };

    history.replaceState = function(...args) {
      //console.log('📍 ReplaceState detected');
      originalReplaceState.apply(history, args);
      setTimeout(() => {
        if (window.location.href !== currentUrl) {
          currentUrl = window.location.href;
          (window as any).userPresenceService?.trackPageVisit();
        }
      }, 100);
    };
  }

  async trackPageVisit() {
    if (!this.isTracking) return;

    this.pageStartTime = Date.now();
    
    const trackingData: TrackingData = {
      url: window.location.href,
      pageTitle: document.title || 'Ellipsis App',
      referrer: document.referrer || '',
      sessionId: this.sessionId,
      userAgent: navigator.userAgent,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      deviceType: this.getDeviceType(),
      browser: this.getBrowser(),
    };

    //console.log('📊 Tracking page visit:', trackingData);

    try {
      await this.sendTrackingData(trackingData);
      //console.log('✅ Successfully tracked page visit');
    } catch (error) {
      console.error('❌ Failed to track page visit:', error);
    }
  }

  private trackPageDuration() {
    if (this.pageStartTime === 0) return;

    const duration = Math.floor((Date.now() - this.pageStartTime) / 1000);
    //console.log(`⏱️ Page duration: ${duration} seconds`);

    if (duration > 0) {
      // You could send a duration update here if needed
      this.sendDurationUpdate(duration);
    }
  }

  private async sendDurationUpdate(duration: number) {
    // This could be used to update the duration of the current visit
    //console.log(`📊 Would update duration to ${duration}s for session ${this.sessionId}`);
  }

  private async sendTrackingData(data: TrackingData) {
    const token = localStorage.getItem('authToken');
    const endpoint = token ? '/tracking/visit/authenticated' : '/tracking/visit';
    
    //console.log(`🌐 Sending tracking data to: ${getApiUrl(endpoint)}`);
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      //console.log('🔐 Using authenticated tracking');
    } else {
      //console.log('👤 Using anonymous tracking');
    }

    const response = await fetch(getApiUrl(endpoint), {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    //console.log('📤 Tracking response:', result);
    return result;
  }

  private getDeviceType(): string {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android')) {
      return 'mobile';
    } else if (ua.includes('tablet') || ua.includes('ipad')) {
      return 'tablet';
    }
    return 'desktop';
  }

  private getBrowser(): string {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('chrome')) return 'Chrome';
    if (ua.includes('firefox')) return 'Firefox';
    if (ua.includes('safari')) return 'Safari';
    if (ua.includes('edge')) return 'Edge';
    return 'Unknown';
  }

  // Public methods for admin dashboard
  async getUserPresence(query: any = {}) {
    const token = localStorage.getItem('authToken');
    const params = new URLSearchParams();
    
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString());
      }
    });

    const response = await fetch(getApiUrl(`/admin/user-presence?${params}`), {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    return await response.json();
  }

  async getPresenceAnalytics() {
    const token = localStorage.getItem('authToken');
    
    const response = await fetch(getApiUrl('/admin/user-presence/analytics'), {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    return await response.json();
  }

  async getActiveUsers(minutes: number = 30) {
    const token = localStorage.getItem('authToken');
    
    const response = await fetch(getApiUrl(`/admin/user-presence/active?minutes=${minutes}`), {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    return await response.json();
  }

  // Control methods
  startTracking() {
    this.isTracking = true;
    //console.log('▶️ User tracking started');
  }

  stopTracking() {
    this.isTracking = false;
    //console.log('⏹️ User tracking stopped');
  }
}

// Create and export a singleton instance
export const userPresenceService = new UserPresenceService();

// Make it available globally for debugging
(window as any).userPresenceService = userPresenceService;

//console.log('🎯 UserPresenceService loaded and available globally');

export default userPresenceService;
